import { Badge, Col, Container, Row, Card, CardBody, Spinner } from 'react-bootstrap';
import { useServicesPage } from '@/hooks/useServicesPage';
import { getMediaUrl } from '@/utils/imageUrl';

const TransferServices = ({ sectionData: propSectionData }) => {
  const { section: fetchedSection, loading, error } = useServicesPage('transfer_services');
  const section = propSectionData || fetchedSection;

  const bgColor = section?.background_color || 'light';
  const bgEffect = section?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  // Apply gradient effect if selected
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }

  if (loading && !propSectionData) {
    return (
      <section className={`py-5 py-lg-6 ${bgClass} services-section transfer-services-section`}>
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </section>
    );
  }

  if (error || !section || !section.is_active) {
    return null;
  }

  const items = section.items || [];
  
  // Separate items by type
  const cardItems = items.filter(item => item.item_type === 'card');
  const featureItems = items.filter(item => item.item_type === 'feature');
  const experienceStatement = cardItems.find(item => item.title?.includes('Experience') || item.title?.includes('Statement'));
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';

  const parseItemContent = (item) => {
    if (!item?.content) return {};
    if (typeof item.content === 'string') {
      try {
        return JSON.parse(item.content);
      } catch {
        return {};
      }
    }
    return item.content || {};
  };

  const normalizeBullets = (content) => {
    if (Array.isArray(content?.bullets)) return content.bullets.filter(Boolean);
    if (Array.isArray(content?.airports)) {
      return content.airports
        .map((airport) => {
          if (!airport) return '';
          const name = airport.name || '';
          const desc = airport.description ? ` — ${airport.description}` : '';
          return `${name}${desc}`.trim();
        })
        .filter(Boolean);
    }
    return [];
  };

  const dualBoxItems = cardItems.filter((item) => parseItemContent(item)?.layout === 'dual_box');
  const legacyBoxItems = cardItems
    .filter(item => item !== experienceStatement)
    .map((item) => {
      const content = parseItemContent(item);
      const backgroundImage = item.background_image || content.image || item.image || null;
      const bullets = normalizeBullets(content);
      return {
        ...item,
        content: {
          ...content,
          layout: 'dual_box',
          bullets,
          image_style: content.image_style || (backgroundImage ? 'background' : 'header'),
          image: backgroundImage || content.image || ''
        }
      };
    });
  const boxItems = dualBoxItems.length > 0 ? dualBoxItems : legacyBoxItems;

  return (
    <section className={`py-5 py-lg-6 ${bgClass} services-section transfer-services-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`} id="transfer-services">
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container className={hasDotsEffect ? 'position-relative' : ''}>
        {/* Header Section */}
        <Row className="mb-4">
          <Col>
            {section.subtitle && (
              <Badge bg="primary" className="bg-opacity-10 text-primary mb-3">
                {section.subtitle}
              </Badge>
            )}
            {section.title && (
              <h2 className="sec-title mb-3">
                {section.title}
              </h2>
            )}
            {section.intro_text && (
              <p className="lead intro-text mb-0" dangerouslySetInnerHTML={{ __html: section.intro_text }} />
            )}
          </Col>
        </Row>

        {/* Dual Box Cards */}
        {boxItems.length > 0 && (
          <Row className="g-4 mb-4">
            {boxItems.map((item, idx) => {
              const Icon = item.IconComponent;
              const content = parseItemContent(item);
              const bullets = normalizeBullets(content);
              const imageStyle = (content.image_style || 'background').toLowerCase();
              const imageSrc = getMediaUrl(item.background_image || content.image);
              const hasBackground = imageStyle === 'background' && imageSrc;
              const hasHeader = imageStyle === 'header' && imageSrc;
              const badgeText = content.badge || '';
              const subtitleText = content.subtitle || '';

              return (
                <Col key={item.id || idx} lg={6}>
                  <Card className={`border-0 shadow-sm h-100 dual-box-card ${hasBackground ? 'dual-box-card--background text-white' : ''}`}>
                    {hasBackground && (
                      <>
                        <div className="dual-box-card-bg" style={{ backgroundImage: `url(${imageSrc})` }} />
                        <div className="dual-box-card-overlay" />
                      </>
                    )}
                    {hasHeader && (
                      <div className="dual-box-card-image">
                        <img src={imageSrc} alt={item.title || 'Card image'} />
                      </div>
                    )}
                    <CardBody className={`dual-box-card-content ${hasBackground ? 'position-relative' : ''}`}>
                      {Icon && (
                        <div className={`icon-lg ${hasBackground ? 'dual-box-icon' : `bg-${item.icon_color || 'primary'} bg-opacity-10 text-${item.icon_color || 'primary'}`} rounded-circle flex-centered ${hasBackground ? '' : 'mb-3'}`}>
                          <Icon size={24} />
                        </div>
                      )}
                      <div className={hasBackground ? 'dual-box-content-bottom' : ''}>
                      {(badgeText || item.title) && (
                        <div className="dual-box-card-title-row mb-2">
                          {item.title && (
                            <h4 className={`fw-bold mb-0 ${hasBackground ? 'text-white' : ''}`} style={{ flex: '1 1 auto', minWidth: 0 }}>
                              {item.title}
                            </h4>
                          )}
                            {badgeText && (
                              <Badge
                                className="dual-box-badge"
                                style={{ flexShrink: 0 }}
                              >
                                {badgeText}
                              </Badge>
                            )}
                        </div>
                      )}
                        {subtitleText && (
                          <div className={`text-uppercase small mb-2 ${hasBackground ? 'text-white-75' : 'text-muted'}`}>
                            {subtitleText}
                          </div>
                        )}
                        {item.description && (
                          <p
                            className={`mb-3 ${hasBackground ? 'text-white-75' : ''}`}
                            style={{ color: hasBackground ? undefined : '#495057', lineHeight: '1.6' }}
                            dangerouslySetInnerHTML={{ __html: item.description }}
                          />
                        )}
                      {bullets.length > 0 && (
                        <div className={hasBackground ? 'dual-box-glass' : ''}>
                          <ul className={`mb-0 ps-3 ${hasBackground ? 'text-white-75' : 'text-muted'}`} style={{ marginTop: 0, marginBottom: 0 }}>
                            {bullets.map((bullet, bulletIdx) => (
                              <li key={`${bullet}-${bulletIdx}`} className={bulletIdx === bullets.length - 1 ? 'mb-0' : 'mb-2'}>
                                {bullet}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Features Section */}
        {featureItems.length > 0 && (
          <div className="mb-4">
          <Row className="g-4">
            {featureItems.map((item) => {
              const Icon = item.IconComponent;
              const colorMap = {
                'primary': 'primary',
                'success': 'success',
                'info': 'info',
                'warning': 'warning',
                'danger': 'danger'
              };
              const bgColor = colorMap[item.icon_color] || 'primary';
              
              return (
                  <Col key={item.id} md={6} lg={4}>
                    <div className="text-center h-100 p-3">
                    {Icon && (
                      <div className={`icon-xl bg-${bgColor} bg-opacity-10 text-${bgColor} rounded-circle flex-centered mx-auto mb-3`}>
                        <Icon size={28} />
                      </div>
                    )}
                      {item.title && (
                        <h5 className="fw-bold mb-2" style={{ color: '#000000' }}>
                          {item.title}
                        </h5>
                      )}
                    {item.description && (
                        <p className="mb-0" style={{ color: '#495057', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: item.description }} />
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
          </div>
        )}

        {/* Experience Statement */}
        {experienceStatement && (
          <Row className="mt-4">
            <Col lg={10} className="mx-auto">
              <div className="bg-white rounded-4 p-4 p-lg-5 shadow-sm border-start border-4 border-primary">
                {experienceStatement.description && (
                  <p className="lead intro-text mb-0" dangerouslySetInnerHTML={{ __html: experienceStatement.description }} />
                )}
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </section>
  );
};

export default TransferServices;

